import { useCallback, useEffect, useReducer, useRef } from "react";
import { AssistantServices, NotistackServices, type MessageAttachment } from "shared/services";
import type {
	AssistantMessageDTO,
	SSEConfirmationMessage,
	SSEConfirmationPending,
	SSEConfirmationRequired,
	SSEToolCall,
	StreamCallbacks,
} from "shared/services/AssistantServices";
import type { MessageToolCall } from "shared/types/MessageContracts";
import type { ExtractedMessageConfirmation } from "shared/utils";
import {
	extractConfirmationsFromMessage,
	extractTextFromMessage,
	firstMessageFromPossibleArray,
	isLegacyAssistantMessageDTO,
	isPublicMessageDTO,
	roleToSenderType,
	toConfirmationStatusUI,
} from "shared/utils";
import { useStreamBuffer } from "./useStreamBuffer";

// ── State & Reducer ───────────────────────────────────────────────

export interface AssistantChatMessage {
	id: string;
	content: string;
	senderType: "user" | "assistente" | "confirmation" | "oauth_action";
	pending?: boolean;
	error?: boolean;
	errorMessage?: string;
	thinking?: string;
	createdAt: string;
	isStreaming?: boolean;
	metadata?: Record<string, any>;
	/** EK-408 polish: anexos persistidos exibidos no histórico. */
	attachments?: import("shared/types/MessageContracts").PublicMessageAttachmentDTO[];
}

interface AssistantState {
	conversationId: string | null;
	messages: AssistantChatMessage[];
	isStreaming: boolean;
	loadingChat: boolean;
	activeTools: Map<string, string>;
}

const INITIAL_STATE: AssistantState = {
	conversationId: null,
	messages: [],
	isStreaming: false,
	loadingChat: true,
	activeTools: new Map(),
};

type Action =
	| { type: "HISTORY_LOADED"; conversationId: string; messages: AssistantChatMessage[] }
	| { type: "HISTORY_EMPTY" }
	| { type: "HISTORY_FAILED" }
	| { type: "CONVERSATION_CREATED"; conversationId: string }
	| { type: "MESSAGES_UPDATED"; updater: (msgs: AssistantChatMessage[]) => AssistantChatMessage[] }
	| { type: "STREAMING_STARTED"; userMsg: AssistantChatMessage; assistantMsg: AssistantChatMessage }
	| { type: "STREAMING_STOPPED" }
	| { type: "STREAMING_ERROR"; assistantMsgId: string; errorMsg: string }
	| { type: "TOOL_STARTED"; id: string; name: string }
	| { type: "TOOL_FINISHED"; id: string }
	| { type: "RESUME_STARTED"; confirmationId: string; approved: boolean; aiMsg: AssistantChatMessage };

function reducer(state: AssistantState, action: Action): AssistantState {
	switch (action.type) {
		case "HISTORY_LOADED":
			return { ...state, conversationId: action.conversationId, messages: action.messages, loadingChat: false };

		case "HISTORY_EMPTY":
		case "HISTORY_FAILED":
			return { ...state, loadingChat: false };

		case "CONVERSATION_CREATED":
			return { ...state, conversationId: action.conversationId };

		case "MESSAGES_UPDATED":
			return { ...state, messages: action.updater(state.messages) };

		case "STREAMING_STARTED":
			return { ...state, messages: [...state.messages, action.userMsg, action.assistantMsg], isStreaming: true };

		case "STREAMING_STOPPED":
			return { ...state, isStreaming: false, activeTools: new Map() };

		case "STREAMING_ERROR":
			return {
				...state,
				messages: state.messages.map((m) =>
					m.id === action.assistantMsgId ? { ...m, thinking: undefined, error: true, errorMessage: action.errorMsg } : m,
				),
				isStreaming: false,
				activeTools: new Map(),
			};

		case "TOOL_STARTED": {
			const tools = new Map(state.activeTools);
			tools.set(action.id, action.name);
			return { ...state, activeTools: tools };
		}

		case "TOOL_FINISHED": {
			const tools = new Map(state.activeTools);
			tools.delete(action.id);
			return { ...state, activeTools: tools };
		}

		case "RESUME_STARTED":
			return {
				...state,
				messages: [
					...state.messages.map((m) => {
						if (m.senderType === "confirmation" && m.metadata?.confirmationId === action.confirmationId) {
							return { ...m, metadata: { ...m.metadata, status: action.approved ? "approved" : "denied" } };
						}
						return m;
					}),
					action.aiMsg,
				],
				isStreaming: true,
			};

		default:
			return state;
	}
}

// ── Helpers ───────────────────────────────────────────────────────

/**
 * EK-408: faz upload de cada arquivo para storage persistente e retorna
 * referências `{attachmentId}` em vez de base64 inline. Substitui o fluxo
 * antigo (base64 → payload gigante, anexo perdido após response do LLM).
 */
async function filesToAttachments(workspaceId: string, files: File[]): Promise<MessageAttachment[]> {
	const { WorkspaceServices } = await import("shared/services");
	return Promise.all(
		files.map(async (file) => {
			const uploaded = await WorkspaceServices.uploadChatAttachment(workspaceId, file);
			return { attachmentId: uploaded.id, filename: uploaded.filename, mimeType: uploaded.mimeType };
		}),
	);
}

const CONFIRMATION_MSG_PREFIX = "confirmation-";

function parseToolArguments(rawArgs: string): Record<string, any> | string {
	try {
		return JSON.parse(rawArgs);
	} catch {
		return rawArgs;
	}
}

function buildConfirmationChatMessage(confirmation: ExtractedMessageConfirmation, createdAt: string): AssistantChatMessage {
	return {
		id: `${CONFIRMATION_MSG_PREFIX}${confirmation.confirmationId}`,
		content: confirmation.description,
		senderType: "confirmation",
		createdAt,
		metadata: {
			confirmationId: confirmation.confirmationId,
			status: confirmation.status,
			reason: confirmation.reason,
			toolCall: {
				id: confirmation.toolCall.id,
				name: confirmation.toolName,
				arguments: parseToolArguments(confirmation.toolCall.function.arguments),
			},
		},
	};
}

function mapLegacyMessageToChatMessages(message: AssistantMessageDTO): AssistantChatMessage[] {
	if (!isLegacyAssistantMessageDTO(message)) return [];
	if (message.metadata?.isConfirmationResponse) return [];

	const content = (message.content || "").trim();

	if (message.senderType === "confirmation") {
		const confirmations = extractConfirmationsFromMessage(message);
		if (confirmations.length > 0) {
			return confirmations.map((confirmation) => buildConfirmationChatMessage(confirmation, message.createdAt));
		}

		return [
			{
				id: `${CONFIRMATION_MSG_PREFIX}${message.metadata?.confirmationId || message.id}`,
				content,
				senderType: "confirmation",
				createdAt: message.createdAt,
				metadata: {
					...(message.metadata ?? {}),
					status: toConfirmationStatusUI(message.metadata?.status),
				},
			},
		];
	}

	if (message.senderType !== "user" && message.senderType !== "assistente") return [];

	return [
		{
			id: `msg-${message.id}`,
			content,
			senderType: message.senderType,
			createdAt: message.createdAt,
			metadata: message.metadata,
		},
	];
}

function mapPublicMessageToChatMessages(message: AssistantMessageDTO): AssistantChatMessage[] {
	if (!isPublicMessageDTO(message)) return [];
	if (message.metadata?.isConfirmationResponse) return [];

	const mapped: AssistantChatMessage[] = [];
	const senderType = roleToSenderType(message.role);
	const text = extractTextFromMessage(message);

	if (senderType && text) {
		mapped.push({
			id: `msg-${message.id}`,
			content: text,
			senderType,
			createdAt: message.createdAt,
			// EK-408 polish: propaga anexos do histórico para a UI exibir
			// preview (PDF/vídeo/áudio/imagem/etc).
			attachments: (message as any).attachments ?? undefined,
			// EK-409: propaga metadata p/ Chat.Message poder renderizar
			// metadata.error_alert (card vermelho) e outros indicadores.
			metadata: message.metadata,
		});
	}

	if (message.role === "assistant") {
		const confirmations = extractConfirmationsFromMessage(message);
		for (const confirmation of confirmations) {
			mapped.push(buildConfirmationChatMessage(confirmation, message.createdAt));
		}
	}

	return mapped;
}

function mapAssistantMessageToChatMessages(message: AssistantMessageDTO): AssistantChatMessage[] {
	if (isLegacyAssistantMessageDTO(message)) return mapLegacyMessageToChatMessages(message);
	return mapPublicMessageToChatMessages(message);
}

function upsertConfirmationMessages(current: AssistantChatMessage[], confirmations: AssistantChatMessage[]): AssistantChatMessage[] {
	let next = [...current];

	for (const confirmation of confirmations) {
		const confirmationId = confirmation.metadata?.confirmationId;
		if (!confirmationId) {
			next.push(confirmation);
			continue;
		}

		const idx = next.findIndex((msg) => msg.senderType === "confirmation" && msg.metadata?.confirmationId === confirmationId);
		if (idx >= 0) {
			next[idx] = confirmation;
		} else {
			next.push(confirmation);
		}
	}

	return next;
}

function mapConfirmationStatusToDisplay(status?: string): "pending" | "approved" | "denied" {
	return toConfirmationStatusUI(status);
}

function buildConfirmationFromToolCall(toolCall: MessageToolCall, confirmation: SSEConfirmationRequired): AssistantChatMessage {
	return {
		id: `${CONFIRMATION_MSG_PREFIX}${confirmation.confirmationId}`,
		content: confirmation.description || `Deseja executar ${toolCall.function.name}?`,
		senderType: "confirmation",
		createdAt: new Date().toISOString(),
		metadata: {
			confirmationId: confirmation.confirmationId,
			status: "pending",
			toolCall: {
				id: toolCall.id,
				name: toolCall.function.name,
				arguments: parseToolArguments(toolCall.function.arguments),
			},
		},
	};
}

const OAUTH_ACTION_MSG_PREFIX = "oauth-action-";

interface OAuthActionPayload {
	memberName?: string;
	teamName?: string;
	items: Array<{ slug: string; displayName: string; contractedMcpId?: string | null; envName?: string | null }>;
}

/**
 * Detecta tool_result com `error: "OAUTH_CONNECTION_REQUIRED"` (ou `MISSING_MCP_OAUTH`)
 * e converte em uma mensagem renderizável de action card.
 */
function buildOAuthActionMessageFromToolResult(data: { id: string; name: string; result?: any }): AssistantChatMessage | null {
	let result = data?.result;
	if (typeof result === "string") {
		try {
			result = JSON.parse(result);
		} catch {
			return null;
		}
	}
	if (!result || typeof result !== "object") return null;

	const errorCode = typeof result.error === "string" ? result.error : null;
	const isOAuthError = errorCode === "OAUTH_CONNECTION_REQUIRED" || errorCode === "MISSING_MCP_OAUTH";
	if (!isOAuthError) return null;

	const payload = (result.data ?? {}) as Record<string, any>;

	// Normaliza diferentes formatos de payload (hire_member usa `pendingOAuth`,
	// assign_task usa `missingOAuth`).
	const rawList: any[] = Array.isArray(payload.pendingOAuth)
		? payload.pendingOAuth
		: Array.isArray(payload.missingOAuth)
			? payload.missingOAuth
			: [];

	const items = rawList
		.filter((entry) => entry && typeof entry === "object")
		.map((entry) => ({
			slug: String(entry.slug ?? ""),
			displayName: String(entry.displayName ?? entry.slug ?? "Ferramenta"),
			contractedMcpId: typeof entry.contractedMcpId === "string" ? entry.contractedMcpId : null,
			envName: typeof entry.envName === "string" ? entry.envName : null,
		}))
		.filter((entry) => entry.slug.length > 0);

	if (items.length === 0) return null;

	const meta: OAuthActionPayload = {
		memberName: typeof payload.memberName === "string" ? payload.memberName : undefined,
		teamName: typeof payload.teamName === "string" ? payload.teamName : undefined,
		items,
	};

	return {
		id: `${OAUTH_ACTION_MSG_PREFIX}${data.id}`,
		content: "",
		senderType: "oauth_action",
		createdAt: new Date().toISOString(),
		metadata: {
			toolCallId: data.id,
			toolName: data.name,
			oauth: meta,
		},
	};
}

/**
 * Insere ou substitui um action card OAuth (deduplicação por id).
 */
function upsertOAuthActionMessage(current: AssistantChatMessage[], action: AssistantChatMessage): AssistantChatMessage[] {
	const idx = current.findIndex((m) => m.id === action.id);
	if (idx >= 0) {
		const next = [...current];
		next[idx] = action;
		return next;
	}
	return [...current, action];
}

// ── Hook ──────────────────────────────────────────────────────────

export const useAssistantMessages = (workspaceId: string | null) => {
	const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

	const stateRef = useRef(state);
	stateRef.current = state;

	const workspaceIdRef = useRef(workspaceId);
	workspaceIdRef.current = workspaceId;

	const wsSubscriptionRef = useRef<{ stop: () => void } | null>(null);
	const sseControllerRef = useRef<AbortController | null>(null);
	const activeAssistantMsgIdRef = useRef<string | null>(null);
	const activeStreamTransportRef = useRef<"ws" | "sse">("ws");
	const streamHasActivityRef = useRef(false);
	const streamWarningShownRef = useRef(false);

	// ── Stream buffer (renderizacao suave RNF002) ───────────────
	const streamBuffer = useStreamBuffer({
		charsPerFrame: 3,
		onFlush: (accumulated) => {
			const msgId = activeAssistantMsgIdRef.current;
			if (!msgId) return;
			dispatch({
				type: "MESSAGES_UPDATED",
				updater: (msgs) => msgs.map((m) => (m.id === msgId ? { ...m, content: accumulated, thinking: undefined } : m)),
			});
		},
	});

	const cleanupWS = useCallback(() => {
		wsSubscriptionRef.current?.stop();
		wsSubscriptionRef.current = null;
	}, []);

	const cleanupSSE = useCallback(() => {
		sseControllerRef.current?.abort();
		sseControllerRef.current = null;
	}, []);

	const stopStreaming = useCallback(() => {
		streamBuffer.flush();

		const msgId = activeAssistantMsgIdRef.current;
		if (msgId) {
			dispatch({
				type: "MESSAGES_UPDATED",
				updater: (msgs) => msgs.map((m) => (m.id === msgId ? { ...m, isStreaming: false } : m)),
			});
		}

		activeAssistantMsgIdRef.current = null;
		activeStreamTransportRef.current = "ws";
		streamHasActivityRef.current = false;
		streamWarningShownRef.current = false;
		dispatch({ type: "STREAMING_STOPPED" });
		cleanupSSE();
		cleanupWS();
	}, [cleanupSSE, cleanupWS, streamBuffer]);

	// Cleanup ao desmontar
	useEffect(
		() => () => {
			cleanupSSE();
			cleanupWS();
		},
		[cleanupSSE, cleanupWS],
	);

	// Carregar historico de conversas ao montar
	useEffect(() => {
		let cancelled = false;

		const loadHistory = async () => {
			if (!workspaceId) {
				dispatch({ type: "HISTORY_EMPTY" });
				return;
			}

			// Não recarregar enquanto há um stream ativo — evita zerar tokens em tempo real.
			if (stateRef.current.isStreaming) return;

			// Não recarregar se já existe conversação carregada — evita sobrescrever mensagens
			// não salvas ou durante interações ativas.
			if (stateRef.current.conversationId && stateRef.current.messages.length > 0) return;

			try {
				const conversations = await AssistantServices.listConversations(workspaceId);
				if (cancelled) return;

				if (conversations.length > 0) {
					const latest = [...conversations].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];
					const data = await AssistantServices.getConversation(workspaceId, latest.id, 100, 0);
					if (cancelled) return;

					dispatch({
						type: "HISTORY_LOADED",
						conversationId: latest.id,
						messages: data.messages.flatMap((message) => mapAssistantMessageToChatMessages(message)),
					});
				} else {
					dispatch({ type: "HISTORY_EMPTY" });
				}
			} catch {
				if (!cancelled) dispatch({ type: "HISTORY_FAILED" });
			}
		};

		loadHistory();
		return () => {
			cancelled = true;
		};
	}, [workspaceId]);

	// ── Inscricao WebSocket ─────────────────────────────────────

	const subscribeToWS = useCallback(
		(convId: string, assistantMsgId: string) => {
			cleanupWS();
			activeAssistantMsgIdRef.current = assistantMsgId;

			const updateAssistantMsg = (updater: (msg: AssistantChatMessage) => AssistantChatMessage) => {
				dispatch({ type: "MESSAGES_UPDATED", updater: (msgs) => msgs.map((m) => (m.id === assistantMsgId ? updater(m) : m)) });
			};

			const callbacks: StreamCallbacks = {
				onProcessingStarted: () => {
					streamHasActivityRef.current = true;
					updateAssistantMsg((m) => ({ ...m, thinking: "Processando..." }));
				},

				onToken: (data) => {
					if (activeStreamTransportRef.current !== "ws") return;
					streamHasActivityRef.current = true;
					const token = data.token || data.content || "";
					if (token) streamBuffer.push(token);
				},

				onToolCall: (data: SSEToolCall) => {
					streamHasActivityRef.current = true;
					dispatch({ type: "TOOL_STARTED", id: data.id, name: data.name });
					updateAssistantMsg((m) => ({ ...m, thinking: m.content ? undefined : `Executando: ${data.name}...` }));
				},

				onToolResult: (data) => {
					streamHasActivityRef.current = true;
					dispatch({ type: "TOOL_FINISHED", id: data.id });

					// ── Action card OAuth ──────────────────────────────
					// Quando uma tool retorna `error: "OAUTH_CONNECTION_REQUIRED"`
					// (ex.: `hire_member` detecta MCPs Google sem token), injetamos
					// um card inline no chat com botões para iniciar o OAuth direto.
					const oauthMsg = buildOAuthActionMessageFromToolResult(data);
					if (oauthMsg) {
						dispatch({
							type: "MESSAGES_UPDATED",
							updater: (msgs) => upsertOAuthActionMessage(msgs, oauthMsg),
						});
					}
				},

				onConfirmationRequired: (data: SSEConfirmationRequired) => {
					streamHasActivityRef.current = true;
					updateAssistantMsg((m) => ({ ...m, thinking: m.content ? undefined : "Aguardando sua confirmacao..." }));

					if (data.toolCallId && data.toolName) {
						const toolCall: MessageToolCall = {
							id: data.toolCallId,
							type: "function",
							function: {
								name: data.toolName,
								arguments: JSON.stringify(data.arguments ?? {}),
							},
							description: data.description,
						};

						const confirmationMessage = buildConfirmationFromToolCall(toolCall, data);

						dispatch({
							type: "MESSAGES_UPDATED",
							updater: (msgs) => upsertConfirmationMessages(msgs, [confirmationMessage]),
						});
					}
				},

				onConfirmationPending: (data: SSEConfirmationPending) => {
					streamHasActivityRef.current = true;
					updateAssistantMsg((m) => ({ ...m, thinking: m.content ? undefined : "Aguardando sua confirmacao..." }));

					if (!data.confirmationId) return;

					dispatch({
						type: "MESSAGES_UPDATED",
						updater: (msgs) =>
							msgs.map((msg) =>
								msg.senderType === "confirmation" && msg.metadata?.confirmationId === data.confirmationId
									? {
											...msg,
											metadata: {
												...msg.metadata,
												status: "pending",
											},
										}
									: msg,
							),
					});
				},

				onConfirmationMessage: (data: SSEConfirmationMessage) => {
					streamHasActivityRef.current = true;
					const confirmationMessages = mapAssistantMessageToChatMessages(data.message).filter((msg) => msg.senderType === "confirmation");

					if (confirmationMessages.length === 0) return;

					dispatch({
						type: "MESSAGES_UPDATED",
						updater: (msgs) => {
							const filtered = msgs.filter((m) => !(m.id === assistantMsgId && !m.content));
							return upsertConfirmationMessages(filtered, confirmationMessages);
						},
					});
				},

				onConfirmationResolved: (data) => {
					streamHasActivityRef.current = true;
					updateAssistantMsg((m) => ({ ...m, thinking: m.content ? undefined : "Continuando..." }));

					dispatch({
						type: "MESSAGES_UPDATED",
						updater: (msgs) =>
							msgs.map((msg) =>
								msg.senderType === "confirmation" && msg.metadata?.confirmationId === data.confirmationId
									? {
											...msg,
											metadata: {
												...msg.metadata,
												status: data.approved ? "approved" : "denied",
											},
										}
									: msg,
							),
					});
				},

				onComplete: (data) => {
					streamHasActivityRef.current = true;
					streamBuffer.flush();
					updateAssistantMsg((m) => ({ ...m, content: data.content || m.content, thinking: undefined }));
				},

				onMessageSaved: (data) => {
					streamHasActivityRef.current = true;
					const savedUserMsg = firstMessageFromPossibleArray(data.userMessage);
					const savedAiMsg = firstMessageFromPossibleArray(data.assistantMessage ?? data.leaderMessage);

					const savedAiText = savedAiMsg ? extractTextFromMessage(savedAiMsg) : "";
					const savedAiConfirmations = savedAiMsg
						? mapAssistantMessageToChatMessages(savedAiMsg).filter((msg) => msg.senderType === "confirmation")
						: [];

					dispatch({
						type: "MESSAGES_UPDATED",
						updater: (msgs) => {
							let next = msgs.map((m) => {
								if (savedUserMsg && m.senderType === "user" && m.id.startsWith("user-")) {
									return {
										...m,
										id: `msg-${savedUserMsg.id}`,
										content: extractTextFromMessage(savedUserMsg),
										createdAt: savedUserMsg.createdAt,
									};
								}

								if (savedAiMsg && m.id === assistantMsgId) {
									const updatedId = `msg-${savedAiMsg.id}`;
									activeAssistantMsgIdRef.current = updatedId;

									return {
										...m,
										id: updatedId,
										content: savedAiText || m.content,
										thinking: undefined,
										createdAt: savedAiMsg.createdAt,
										isStreaming: m.isStreaming,
									};
								}

								return m;
							});

							if (savedAiMsg && savedAiConfirmations.length > 0) {
								const savedAiId = `msg-${savedAiMsg.id}`;

								if (!savedAiText) {
									next = next.filter((msg) => !(msg.id === savedAiId && msg.senderType === "assistente" && !msg.content));
								}

								next = upsertConfirmationMessages(next, savedAiConfirmations);
							}

							return next;
						},
					});
				},

				onProcessingStatus: (data) => {
					streamHasActivityRef.current = true;
					if (data.currentStep) {
						updateAssistantMsg((m) => (!m.content ? { ...m, thinking: data.currentStep } : m));
					}
				},

				onProcessingCompleted: () => stopStreaming(),

				onProcessingError: (data) => {
					streamHasActivityRef.current = true;
					const errorMsg = data.error || "Erro no processamento.";
					const currentAssistantMsgId = activeAssistantMsgIdRef.current ?? assistantMsgId;
					dispatch({ type: "STREAMING_ERROR", assistantMsgId: currentAssistantMsgId, errorMsg });
					cleanupSSE();
					cleanupWS();
					NotistackServices.showErrorMessage(errorMsg);
				},

				onDone: () => {
					streamHasActivityRef.current = true;
					stopStreaming();
				},

				onError: (data) => {
					streamHasActivityRef.current = true;
					const errorMsg = data?.message || "Ocorreu um erro inesperado.";
					const currentAssistantMsgId = activeAssistantMsgIdRef.current ?? assistantMsgId;
					dispatch({ type: "STREAMING_ERROR", assistantMsgId: currentAssistantMsgId, errorMsg });
					cleanupSSE();
					cleanupWS();
				},
			};

			wsSubscriptionRef.current = AssistantServices.subscribeToConversation(convId, callbacks);
		},
		[cleanupSSE, cleanupWS, stopStreaming, streamBuffer],
	);

	// ── Garantir conversa ───────────────────────────────────────

	const ensureConversation = useCallback(async (): Promise<string> => {
		const currentConvId = stateRef.current.conversationId;
		if (currentConvId) return currentConvId;

		const currentWorkspaceId = workspaceIdRef.current;
		if (!currentWorkspaceId) {
			throw new Error("Workspace nao definido para iniciar conversa.");
		}

		// Antes de criar uma nova conversa, tenta reaproveitar a mais recente do workspace.
		// Isso evita proliferação de conversas e fecha a race condition com `loadHistory`,
		// onde o usuário envia mensagem antes do estado ser hidratado.
		try {
			const conversations = await AssistantServices.listConversations(currentWorkspaceId);
			if (conversations.length > 0) {
				const latest = [...conversations].sort(
					(a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
				)[0];
				dispatch({ type: "CONVERSATION_CREATED", conversationId: latest.id });
				return latest.id;
			}
		} catch {
			// Falha ao listar não é fatal — segue para criar nova conversa.
		}

		const conversation = await AssistantServices.createConversation(currentWorkspaceId, "Configuracao Inicial");
		dispatch({ type: "CONVERSATION_CREATED", conversationId: conversation.id });
		return conversation.id;
	}, []);

	// ── Enviar mensagem ─────────────────────────────────────────

	const sendMessage = useCallback(
		async (content: string, files?: File[]) => {
			const trimmed = content.trim();
			const currentWorkspaceId = workspaceIdRef.current;
			if (stateRef.current.isStreaming || (!trimmed && !files?.length) || !currentWorkspaceId) return;

			let attachments: MessageAttachment[] | undefined;
			if (files?.length) {
				try {
					attachments = await filesToAttachments(currentWorkspaceId, files);
				} catch (err: any) {
					NotistackServices.showErrorMessage(err?.message || "Falha ao subir um ou mais arquivos.");
					return;
				}
			}

			const now = new Date().toISOString();
			const assistantMsgId = `assistant-${Date.now()}`;
			const userMsgContent = trimmed || (files?.length ? files.map((f) => `📎 ${f.name}`).join("\n") : "");
			const userMsg: AssistantChatMessage = { id: `user-${Date.now()}`, content: userMsgContent, senderType: "user", createdAt: now };
			const assistantMsg: AssistantChatMessage = {
				id: assistantMsgId,
				content: "",
				senderType: "assistente",
				thinking: "Pensando...",
				createdAt: now,
				isStreaming: true,
			};

			dispatch({ type: "STREAMING_STARTED", userMsg, assistantMsg });
			streamBuffer.reset();
			streamHasActivityRef.current = false;
			streamWarningShownRef.current = false;

			try {
				const convId = await ensureConversation();
				activeStreamTransportRef.current = "sse";
				subscribeToWS(convId, assistantMsgId);

				cleanupSSE();
				sseControllerRef.current = AssistantServices.sendMessageStream(currentWorkspaceId, convId, trimmed, {
					onToken: (data) => {
						streamHasActivityRef.current = true;
						const token = data.token || data.content || "";
						if (token) streamBuffer.push(token);
					},
					onDone: () => {
						streamBuffer.flush();
						sseControllerRef.current = null;
					},
					onError: (data) => {
						sseControllerRef.current = null;
						const errorMsg = data?.message || "Falha no stream de texto.";
						const errorCode = data?.code;
						const currentAssistantMsgId = activeAssistantMsgIdRef.current ?? assistantMsgId;

						const isPendingConfirmation =
							errorCode === "PENDING_CONFIRMATION" || errorMsg.toLowerCase().includes("pending confirmation");

						if (isPendingConfirmation) {
							// Para o streaming e remove as mensagens otimistas (não foram salvas no banco)
							dispatch({ type: "STREAMING_STOPPED" });
							cleanupWS();

							// Recarrega o histórico para surfaçar o card de confirmação pendente
							const currentWsId = workspaceIdRef.current;
							if (currentWsId) {
								AssistantServices.getConversation(currentWsId, convId, 100, 0)
									.then((result) => {
										dispatch({
											type: "HISTORY_LOADED",
											conversationId: convId,
											messages: result.messages.flatMap((message) => mapAssistantMessageToChatMessages(message)),
										});
										NotistackServices.showWarningMessage(
											"Há uma confirmação pendente. Por favor, confirme ou negue para continuar.",
										);
									})
									.catch(() => {
										NotistackServices.showWarningMessage(
											"Há uma confirmação pendente. Por favor, atualize a página para visualizá-la.",
										);
									});
							}
							return;
						}

						if (!streamHasActivityRef.current) {
							dispatch({ type: "STREAMING_ERROR", assistantMsgId: currentAssistantMsgId, errorMsg });
							cleanupWS();
							NotistackServices.showErrorMessage(errorMsg);
							return;
						}

						dispatch({
							type: "MESSAGES_UPDATED",
							updater: (msgs) =>
								msgs.map((msg) =>
									msg.id === currentAssistantMsgId
										? {
												...msg,
												thinking: msg.content ? undefined : "Conexao instavel no stream. Aguardando consolidacao final...",
											}
										: msg,
								),
						});

						if (!streamWarningShownRef.current) {
							streamWarningShownRef.current = true;
							NotistackServices.showWarningMessage("Conexao de streaming instavel. A resposta final sera consolidada ao concluir.");
						}
					},
				}, attachments);
			} catch (error: any) {
				const errorMsg = error?.message || "Nao foi possivel enviar a mensagem. Tente novamente.";
				dispatch({ type: "STREAMING_ERROR", assistantMsgId, errorMsg });
				cleanupSSE();
				cleanupWS();
				NotistackServices.showErrorMessage(errorMsg);
			}
		},
		[ensureConversation, subscribeToWS, cleanupSSE, cleanupWS, streamBuffer],
	);

	// ── Confirmar/negar ferramenta (inline no chat) ────────────

	const handleConfirmation = useCallback(
		async (confirmationId: string, approved: boolean, reason?: string) => {
			const convId = stateRef.current.conversationId;
			const currentWorkspaceId = workspaceIdRef.current;
			if (!convId || !currentWorkspaceId || stateRef.current.isStreaming) return;

			const assistantMsgId = `assistant-${Date.now()}`;

			dispatch({
				type: "RESUME_STARTED",
				confirmationId,
				approved,
				aiMsg: {
					id: assistantMsgId,
					content: "",
					senderType: "assistente",
					thinking: approved ? "Confirmacao aprovada, executando..." : "Negado, retomando...",
					createdAt: new Date().toISOString(),
					isStreaming: true,
				},
			});

			streamBuffer.reset();
			activeStreamTransportRef.current = "ws";
			streamHasActivityRef.current = false;
			streamWarningShownRef.current = false;
			cleanupSSE();
			subscribeToWS(convId, assistantMsgId);

			try {
				await AssistantServices.confirmToolExecution(currentWorkspaceId, convId, confirmationId, approved, reason);
			} catch (error: any) {
				const errorMsg = error?.message || "Erro ao processar confirmacao.";
				dispatch({ type: "STREAMING_ERROR", assistantMsgId, errorMsg });
				cleanupSSE();
				cleanupWS();
				NotistackServices.showErrorMessage(errorMsg);
			}
		},
		[subscribeToWS, cleanupSSE, cleanupWS, streamBuffer],
	);

	// ── API publica ─────────────────────────────────────────────

	const pendingConfirmationId =
		state.messages.find((m) => m.senderType === "confirmation" && m.metadata?.status === "pending")?.id ?? null;

	return {
		messages: state.messages.map((msg) =>
			msg.senderType === "confirmation"
				? {
						...msg,
						metadata: {
							...(msg.metadata ?? {}),
							status: mapConfirmationStatusToDisplay(msg.metadata?.status),
						},
					}
				: msg,
		),
		isStreaming: state.isStreaming,
		loadingChat: state.loadingChat,
		activeTools: state.activeTools,
		pendingConfirmationId,
		sendMessage,
		cancelStreaming: stopStreaming,
		handleConfirmation,
	};
};
